import type { ServerWidgetConfig } from "./ConfigurationService";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const MODES = new Set(["voice", "chat", "combined"]);
const UI_VARIANTS = new Set(["legacy", "split-switch"]);
/** Match backend resolvePublicWidgetConfig — public wire is L/R only. */
const PUBLIC_POSITIONS = new Set(["bottom-right", "bottom-left"]);

/**
 * Defensive parse of GET /api/widget/config widgetConfig.
 * Drops unknown/invalid keys so garbage wire data cannot enter WidgetConfig.
 * Non-object → null. Empty object after filter → {} (merge no-op).
 * top/custom positions are embed-only and never accepted from the public API.
 */
export function parsePublicWidgetConfig(raw: unknown): ServerWidgetConfig | null {
  if (!isRecord(raw)) return null;

  const out: ServerWidgetConfig = {};

  if (typeof raw.mode === "string" && MODES.has(raw.mode)) {
    out.mode = raw.mode as ServerWidgetConfig["mode"];
  }

  if (typeof raw.uiVariant === "string" && UI_VARIANTS.has(raw.uiVariant)) {
    out.uiVariant = raw.uiVariant as ServerWidgetConfig["uiVariant"];
  }

  // Mirror backend omit-if-unset: only true is authority.
  if (raw.gpsEnabled === true) {
    out.gpsEnabled = true;
  }

  if (typeof raw.widgetName === "string" && raw.widgetName.trim().length > 0) {
    out.widgetName = raw.widgetName.trim();
  }

  if (raw.welcomeMessage === null) {
    out.welcomeMessage = null;
  } else if (
    typeof raw.welcomeMessage === "string" &&
    raw.welcomeMessage.length > 0
  ) {
    out.welcomeMessage = raw.welcomeMessage;
  }

  if (typeof raw.avatarUrl === "string" && raw.avatarUrl.trim().length > 0) {
    out.avatarUrl = raw.avatarUrl.trim();
  }

  if (typeof raw.position === "string" && PUBLIC_POSITIONS.has(raw.position)) {
    out.position = raw.position as ServerWidgetConfig["position"];
  }

  return out;
}
